import { useEffect, useState, useCallback } from 'react';
import { 
  useMsal, 
  useAccount
} from '@azure/msal-react';
import { 
  InteractionRequiredAuthError, 
  SilentRequest, 
  AuthenticationResult, 
  AccountInfo 
} from '@azure/msal-browser';
import { getConfig } from '../config/runtime-config';
import { createLoginRequest } from '../config/msalConfig';
import { oAuthService } from '../services/oAuthService';
import { setUser, clearUser } from '../config/faro';
import type { AuthHookResult } from '@/types/auth';

export interface UseAzureAuthReturn extends AuthHookResult {
  account: AccountInfo | null;
}

/**
 * Custom hook for Azure AD authentication using MSAL
 * Handles token acquisition, refresh, and lifecycle management
 */
export const useAzureAuth = (): UseAzureAuthReturn => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = accounts.length > 0;

  /**
   * Store token in both local state and oAuthService
   */
  const updateToken = useCallback((token: string, expiresOn?: Date) => {
    setAccessToken(token);
    oAuthService.setAccessToken(token, expiresOn);
  }, []);

  /**
   * Clear token from both local state and oAuthService
   */
  const clearToken = useCallback(() => {
    setAccessToken(null);
    oAuthService.clearToken();
  }, []);

  const handleAuthFailure = useCallback(
    async (reason: string, options?: { showError?: boolean }) => {
      const { showError = false } = options || {};
      console.warn('[Auth] Authentication failure:', reason);
      clearToken();
      if (showError) {
        setError(reason);
      }

      if (!account) {
        return;
      }

      try {
        instance.setActiveAccount(null);
        await instance.clearCache();
      } catch (cacheError) {
        console.warn('[Auth] Failed to clear MSAL cache:', cacheError);
      }
    },
    [account, clearToken, instance]
  );

  /**
   * Acquire token silently with automatic popup fallback
   */
  const acquireTokenSilently = useCallback(async (): Promise<string | null> => {
    if (!account) {
      console.warn('[Auth] No account available for token acquisition');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const config = getConfig();
      const loginRequest = createLoginRequest(config);
      
      const silentRequest: SilentRequest = {
        scopes: loginRequest.scopes,
        account: account,
      };

      const response: AuthenticationResult = await instance.acquireTokenSilent(silentRequest);
      updateToken(response.accessToken, response.expiresOn || undefined);
      
      // Track user in Faro after successful token acquisition
      if (response.account) {
        setUser(response.account.localAccountId, {
          username: response.account.username || 'unknown',
          tenantId: response.account.tenantId || 'unknown',
        });
      }
      
      console.log('[Auth] Token acquired silently - expires:', response.expiresOn?.toISOString());
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log('[Auth] Silent token acquisition failed, attempting popup...');
        
        try {
          const config = getConfig();
          const loginRequest = createLoginRequest(config);
          const response = await instance.acquireTokenPopup(loginRequest);
          updateToken(response.accessToken, response.expiresOn || undefined);
          
          console.log('[Auth] Token acquired via popup');
          return response.accessToken;
        } catch (popupError) {
          const errorMsg = popupError instanceof Error ? popupError.message : 'Token acquisition failed';
          console.error('[Auth] Popup token acquisition failed:', errorMsg);
          await handleAuthFailure('Failed to acquire token', { showError: false });
          return null;
        }
      } else {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        console.error('[Auth] Token acquisition error:', errorMsg);
        await handleAuthFailure('Failed to acquire token', { showError: false });
        return null;
      }
    } finally {
      setIsLoading(false);
    }
  }, [account, instance, updateToken, handleAuthFailure]);

  /**
   * Perform interactive login with popup
   */
  const login = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[Auth] Initiating login...');
      const config = getConfig();
      const loginRequest = createLoginRequest(config);
      const response = await instance.loginPopup(loginRequest);
      
      updateToken(response.accessToken, response.expiresOn || undefined);
      
      // Track user in Faro after successful login
      if (response.account) {
        setUser(response.account.localAccountId, {
          username: response.account.username || 'unknown',
          tenantId: response.account.tenantId || 'unknown',
        });
      }
      
      console.log('[Auth] Login successful:', response.account?.username);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Login failed';
      console.error('[Auth] Login failed:', errorMsg);
      setError('Login failed');
      throw error; // Re-throw to allow caller to handle
    } finally {
      setIsLoading(false);
    }
  }, [instance, updateToken]);

  /**
   * Perform logout and clear all tokens
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[Auth] Initiating logout...');
      await instance.logoutPopup({
        postLogoutRedirectUri: globalThis.location.origin,
      });
      
      clearToken();
      
      // Clear user tracking in Faro after logout
      clearUser();
      
      console.log('[Auth] Logout successful');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Logout failed';
      console.error('[Auth] Logout failed:', errorMsg);
      setError('Logout failed');
      throw error; // Re-throw to allow caller to handle
    } finally {
      setIsLoading(false);
    }
  }, [instance, clearToken]);

  // Automatically acquire token when account is available
  useEffect(() => {
    // Only run when MSAL is idle and we have an account but no token
    if (account && !accessToken && inProgress === 'none' && !error) {
      console.log('[Auth] Auto-acquiring token for authenticated account');
      
      // Use void to properly handle the promise without awaiting
      void acquireTokenSilently();
    }
  }, [account, accessToken, inProgress, acquireTokenSilently, error]); // Safe: !accessToken condition prevents loops

  // Register token refresh callback with oAuthService
  // This callback is invoked automatically by oAuthService when API clients request tokens
  useEffect(() => {
    oAuthService.setTokenRefreshCallback(async () => {
      console.log('[Auth] Token refresh callback triggered');
      return await acquireTokenSilently();
    });
  }, [acquireTokenSilently]);

  return {
    account,
    isAuthenticated,
    accessToken,
    // Only show loading when MSAL is actively processing or our operations are in progress
    isLoading: isLoading || inProgress !== 'none',
    error,
    login,
    logout,
    acquireTokenSilently,
  };
};
